import { CustomEventWithDetail, html, View } from 'rune-ts';
import klass from './Textarea.module.scss';
import { font } from '../../../../shared/typography';
import { htmlIf } from '../../../../shared/util';

export type TextareaProps = {
  name: string;
  value?: string;
  placeholder: string;
  // default: 2
  rows?: number;
  // default: 2000
  maxLength?: number;
  custom_klass?: string;
  // default: true
  is_show_count?: boolean;
};

class TextareaFocusoutEvent extends CustomEventWithDetail<TextareaProps> {}
class TextareaFocusinEvent extends CustomEventWithDetail<TextareaProps> {}
class TextareaChangeEvent extends CustomEventWithDetail<TextareaProps> {}

export class Textarea extends View<TextareaProps> {
  static readonly DEFAULT_MAX_LENGTH = 2000;
  static readonly DEFAULT_ROWS = 2;

  static readonly FocusoutEvent = TextareaFocusoutEvent;
  static readonly FocusinEvent = TextareaFocusinEvent;
  static readonly ChangeEvent = TextareaChangeEvent;

  override template() {
    const {
      name,
      placeholder,
      rows = Textarea.DEFAULT_ROWS,
      maxLength = Textarea.DEFAULT_MAX_LENGTH,
      custom_klass = '',
      is_show_count = true,
      value = '',
    } = this.data;

    return html`
      <div class="${klass.text_wrapper} ${custom_klass}">
        <textarea
          name=${name}
          class="${klass.textarea} ${font('16_medium', '14_medium')}"
          placeholder="${placeholder}"
          maxlength="${maxLength}"
          rows="${rows}"
        >${value}</textarea>
        ${htmlIf(html`<div class="${klass.count} ${font('12_medium')}">${this.byteLengthStatus}</div>`, is_show_count)}
      </div>
    `;
  }

  get byteLengthStatus() {
    const { value = '', maxLength = Textarea.DEFAULT_MAX_LENGTH } = this.data;

    return `${value.length} / ${ET('mps2::common::word_length', { length: maxLength })}`;
  }

  public getValue() {
    return this.element().querySelector('textarea')?.value || '';
  }

  public setValue(value: string) {
    const textarea$ = this.element().querySelector('textarea');

    if (textarea$) {
      textarea$.value = value;
      this.data.value = value;
      this.dispatchEvent(Textarea.ChangeEvent, { bubbles: true, detail: this.data });
    }
  }

  protected override onRender() {
    this.delegate('focusin', 'textarea', () => {
      this.data.value = this.getValue();
      this.dispatchEvent(Textarea.FocusinEvent, { bubbles: true, detail: this.data });
    });
    this.delegate('focusout', 'textarea', () => {
      this.data.value = this.getValue();
      this.dispatchEvent(Textarea.FocusoutEvent, { bubbles: true, detail: this.data });
    });
    this.delegate('change', 'textarea', () => {
      this.data.value = this.getValue();
      this._updateCount();
      this.dispatchEvent(Textarea.ChangeEvent, { bubbles: true, detail: this.data });
    });
    this.delegate('keyup', 'textarea', () => {
      this.data.value = this.getValue();
      this.dispatchEvent(Textarea.ChangeEvent, { bubbles: true, detail: this.data });
    });
    this.delegate('input', 'textarea', () => {
      this._updateCount();
    });
  }

  private _updateCount() {
    const count$ = this.element().querySelector(`.${klass.count}`);
    if (count$) {
      count$.innerHTML = this.byteLengthStatus;
    }
  }
}
